import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { MenuGroup, Section } from '@atlaskit/menu';

import { useShareToEntities } from '@confluence/share/entry-points/useShareToEntities';
import { type PageMode } from '@confluence/copy-content-link/entry-points/CopyLinkButton';
import { PAGE_MODE } from '@confluence/share/entry-points/CopyLink';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { withFlags } from '@confluence/flags/entry-points/withFlags';

import type { PresenceContentMode, PresenceContentType } from '../presenceTypes';

import type { SuggestedUser } from './SuggestedCollaboratorsDialog';
import { SuggestedCollaboratorsDialogMenuItem } from './SuggestedCollaboratorsDialogMenuItem';

const i18n = defineMessages({
	sharedFlag: {
		id: 'team-presence.menu.empty-state.shared.flag',
		defaultMessage: 'Content shared',
		description: 'Text shown in a flag when a user clicks the share button.',
	},
});

export type SuggestedCollaboratorsDialogShareSuggestionMenuProps = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	pageMode: PageMode;
	suggestedUsers: SuggestedUser[];
	flags: FlagsStateContainer;
};

export const SuggestedCollaboratorsDialogMenuComponent = ({
	contentId,
	contentType,
	contentMode,
	pageMode,
	suggestedUsers,
	flags,
}: SuggestedCollaboratorsDialogShareSuggestionMenuProps) => {
	const { formatMessage } = useIntl();

	const isEditMode = pageMode === 'edit';
	const { shareToEntities } = useShareToEntities(
		contentId,
		'suggestedCollaboratorsDialog',
		isEditMode ? PAGE_MODE.EDIT : PAGE_MODE.VIEW,
	);

	return (
		// height for 5 suggested users and 12px of padding from Section component, 6 before and 6 after the list
		<MenuGroup maxHeight={232}>
			<Section isScrollable>
				{suggestedUsers.map((suggestedUser) => {
					const handleShareClick = async (): Promise<void> => {
						const result = await shareToEntities(
							[{ id: suggestedUser.id, type: 'user' }],
							isEditMode,
						);

						if (result.success) {
							void flags.showSuccessCircleFlag({
								id: 'team-presence-share-completed-flag',
								title: formatMessage(i18n.sharedFlag),
								isAutoDismiss: true,
							});
						}
					};

					return (
						<SuggestedCollaboratorsDialogMenuItem
							key={suggestedUser.id}
							suggestedUser={suggestedUser}
							onHandleShare={handleShareClick}
							contentId={contentId}
							contentType={contentType}
							contentMode={contentMode}
						/>
					);
				})}
			</Section>
		</MenuGroup>
	);
};

export const SuggestedCollaboratorsDialogMenu = withFlags(
	SuggestedCollaboratorsDialogMenuComponent,
);
